/**
 * StudioContext.tsx
 *
 * Single source of truth for:
 *   - The shared Yjs document (TechPackDoc)
 *   - WebsocketProvider + connection status
 *   - User identity, language, and awareness
 *
 * Also exports useYMap / useYArray -- lightweight hooks that subscribe
 * to Yjs shared types and re-render components on change.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { TechPackDoc, hydrateTechPackCollections, type BomRow } from "../schema.js";
import { colorForId } from "./shared.js";
import { notifyDenied } from "./access_denial_toast.js";
import { getFreshToken } from "./auth.js";

// ---------------------------------------------------------------------------
// useYMap -- subscribe to a Y.Map and return a plain JS Map snapshot
// ---------------------------------------------------------------------------

export function useYMap<T>(ymap: Y.Map<T>): ReadonlyMap<string, T> {
  const [snapshot, setSnapshot] = useState<ReadonlyMap<string, T>>(
    () => new Map(ymap.entries()),
  );
  useEffect(() => {
    const handler = () => setSnapshot(new Map(ymap.entries()));
    ymap.observe(handler);
    return () => ymap.unobserve(handler);
  }, [ymap]);
  return snapshot;
}

// ---------------------------------------------------------------------------
// useYArray -- subscribe to a Y.Array and return a plain array snapshot
// ---------------------------------------------------------------------------

export function useYArray<T>(yarray: Y.Array<T>): readonly T[] {
  const [snapshot, setSnapshot] = useState<readonly T[]>(() => yarray.toArray());
  useEffect(() => {
    const handler = () => setSnapshot(yarray.toArray());
    yarray.observe(handler);
    return () => yarray.unobserve(handler);
  }, [yarray]);
  return snapshot;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// What each client broadcasts about themselves
export interface RawAwarenessState {
  userId: string;
  userName: string;
  color: string;
  activeField: string | null; // "section.fieldKey" -- matches client.ts convention
  languageCode: string;
  timezone: string;
}

// Enriched version for UI rendering
export interface AwarenessUser extends RawAwarenessState {
  clientId: number;
  isLocal: boolean;
  fadingOut: boolean;
}

export type ConnectionStatus = "connected" | "connecting" | "disconnected";

/**
 * Product-layer sync state -- tracks whether local changes are durably
 * confirmed by the server, independent of the raw WebSocket connection state.
 *
 *   synced       -- connected, all changes confirmed by server
 *   dirty        -- local changes not yet confirmed (normal transient state)
 *   reconnecting -- disconnected; Yjs is buffering changes locally
 *   conflict     -- reconnected, but local had updates while offline that
 *                  need review (CRDT merged automatically, but user should confirm)
 */
export type SyncState = "synced" | "dirty" | "reconnecting" | "conflict";

export interface StudioContextValue {
  // Core Yjs
  techPack: TechPackDoc;
  provider: WebsocketProvider;

  // Identity
  userId: string;
  userName: string;
  userColor: string;
  userTimezone: string;
  userRole: 'owner' | 'collaborator' | 'viewer';
  userOrigin: 'specflow' | 'studio';
  roomId: string;

  // Language
  languageCode: string;
  setLanguage: (code: string) => void;

  // Awareness
  setActiveField: (field: string | null) => void;
  awarenessUsers: readonly AwarenessUser[];

  // Connection
  connectionStatus: ConnectionStatus;
  reconnectCount: number;

  // Sync state (product layer)
  syncState: SyncState;

  // Persistence
  lastSaved: Date | null;

  // E-1 v2: true once the initial REST/DB load has settled. The editor form
  // must not render (and therefore must not self-seed placeholder rows/fields)
  // until this is true, or placeholders pre-empt the load and the real content
  // is skipped + persisted-empty.
  hydrated: boolean;
}

// ---------------------------------------------------------------------------
// Deterministic user color from awareness clientId
// ---------------------------------------------------------------------------

const PEER_COLORS = [
  "#f87171", // red-400
  "#fb923c", // orange-400
  "#fbbf24", // amber-400
  "#34d399", // emerald-400
  "#22d3ee", // cyan-400
  "#60a5fa", // blue-400
  "#a78bfa", // violet-400
  "#f472b6", // pink-400
];

export function colorForClient(clientId: number): string {
  return PEER_COLORS[Math.abs(clientId) % PEER_COLORS.length];
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const StudioCtx = createContext<StudioContextValue | null>(null);

export function useStudio(): StudioContextValue {
  const ctx = useContext(StudioCtx);
  if (!ctx) throw new Error("useStudio must be called inside <StudioProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface StudioProviderProps {
  techPack: TechPackDoc;
  provider: WebsocketProvider;
  userId: string;
  userName: string;
  userTimezone: string;
  userRole: 'owner' | 'collaborator' | 'viewer';
  userOrigin: 'specflow' | 'studio';
  roomId: string;
  initialLanguage: string;
  children: ReactNode;
}

export function StudioProvider({
  techPack,
  provider,
  userId,
  userName,
  userTimezone,
  userRole,
  userOrigin,
  roomId,
  initialLanguage,
  children,
}: StudioProviderProps) {
  const [languageCode, setLanguageCodeState] = useState(initialLanguage);

  // Keep languageCode in sync when the TopBar or AccountSidebar changes the language.
  // Without this, TechPackForm (which reads languageCode) diverges from App.tsx's lang.
  useEffect(() => {
    const handler = (e: Event) => {
      const code = (e as CustomEvent<{ lang: string }>).detail?.lang;
      if (code) setLanguageCodeState(code);
    };
    window.addEventListener("languageChanged", handler);
    return () => window.removeEventListener("languageChanged", handler);
  }, []);

  const [awarenessUsers, setAwarenessUsers] = useState<readonly AwarenessUser[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connecting");
  const [reconnectCount, setReconnectCount] = useState(0);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [syncState, setSyncState] = useState<SyncState>("reconnecting");
  const [hydrated, setHydrated] = useState(false);

  // Ref so update/sync callbacks always read current sync state without stale closure
  const syncStateRef = useRef<SyncState>("reconnecting");
  const setSyncStateBoth = useCallback((s: SyncState) => {
    syncStateRef.current = s;
    setSyncState(s);
  }, []);

  // Whether local updates were applied while the connection was down
  const hadLocalUpdatesWhileOffline = useRef(false);

  // Stable color for the local user — derived from userId (UUID), NOT from
  // provider.awareness.clientID which is a random integer re-rolled every
  // WebSocket reconnect and caused the avatar color to change between sessions.
  const userColor = useMemo(() => colorForId(userId), [userId]);

  // Track last-seen state for each clientId so we can show it during fade-out
  const prevUsersRef = useRef<Map<number, AwarenessUser>>(new Map());
  // clientId -> setTimeout handle for the 3s fade-out
  const fadeTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // clientId -> AwarenessUser for clients that have disconnected but are still visible
  const fadingRef = useRef<Map<number, AwarenessUser>>(new Map());

  // Rebuild the full user list from live awareness + fading users
  const syncAwareness = useCallback(() => {
    const rawStates = provider.awareness.getStates() as Map<
      number,
      Partial<RawAwarenessState>
    >;

    const liveUsers = new Map<number, AwarenessUser>();
    for (const [clientId, state] of rawStates) {
      if (!state.userId) continue; // skip uninitialised slots
      const user: AwarenessUser = {
        clientId,
        userId: state.userId,
        userName: state.userName ?? "Unknown",
        color: colorForClient(clientId),
        activeField: state.activeField ?? null,
        languageCode: state.languageCode ?? "en",
        timezone: state.timezone ?? "",
        isLocal: clientId === provider.awareness.clientID,
        fadingOut: false,
      };
      liveUsers.set(clientId, user);
    }

    // Cancel fade-out for clients that came back
    for (const clientId of liveUsers.keys()) {
      if (fadingRef.current.has(clientId)) {
        const t = fadeTimers.current.get(clientId);
        if (t !== undefined) clearTimeout(t);
        fadeTimers.current.delete(clientId);
        fadingRef.current.delete(clientId);
      }
    }

    prevUsersRef.current = liveUsers;

    const all: AwarenessUser[] = [
      ...liveUsers.values(),
      ...[...fadingRef.current.values()].map((u) => ({ ...u, fadingOut: true })),
    ];
    setAwarenessUsers(all);
  }, [provider]);

  // ---- Awareness event handling ----

  useEffect(() => {
    // Publish local identity immediately
    provider.awareness.setLocalState({
      userId,
      userName,
      color: userColor,
      activeField: null,
      languageCode,
      timezone: userTimezone,
    } satisfies RawAwarenessState);

    const onAwarenessChange = ({
      removed,
    }: {
      added: number[];
      updated: number[];
      removed: number[];
    }) => {
      // Schedule 3-second fade for each removed (non-local) client
      for (const clientId of removed) {
        if (clientId === provider.awareness.clientID) continue;
        const lastKnown = prevUsersRef.current.get(clientId);
        if (lastKnown && !fadingRef.current.has(clientId)) {
          fadingRef.current.set(clientId, lastKnown);
          const t = setTimeout(() => {
            fadingRef.current.delete(clientId);
            fadeTimers.current.delete(clientId);
            setAwarenessUsers((prev) =>
              prev.filter((u) => u.clientId !== clientId),
            );
          }, 3000);
          fadeTimers.current.set(clientId, t);
        }
      }
      syncAwareness();
    };

    provider.awareness.on("change", onAwarenessChange);
    syncAwareness();

    return () => {
      provider.awareness.off("change", onAwarenessChange);
      for (const t of fadeTimers.current.values()) clearTimeout(t);
    };
  }, [provider, userId, userName, userColor, syncAwareness]); // languageCode excluded intentionally

  // ---- Connection status + product-layer sync state ----

  useEffect(() => {
    const doc = techPack.ydoc;

    // Raw connection status
    const onStatus = ({ status }: { status: string }) => {
      if (status === "connected") {
        setConnectionStatus("connected");
        setReconnectCount(0);
      } else {
        setConnectionStatus(
          (provider as unknown as { wsUnsuccessfulReconnects: number })
            .wsUnsuccessfulReconnects > 0
            ? "disconnected"
            : "connecting",
        );
      }
    };

    const onConnectionLost = () => {
      setReconnectCount((c) => c + 1);
      setConnectionStatus("connecting");
      setSyncStateBoth("reconnecting");
    };

    // Sync event -- fires when the server confirms full state (i.e. the
    // client's WebsocketProvider.synced flips true after processing a real
    // messageYjsSyncStep2 -- proof the E-4 wire-framing fix delivers content
    // over WS, not just that the socket connected without error).
    const onSync = (isSynced: boolean) => {
      if (!isSynced) return;
      setLastSaved(new Date());
      if (hadLocalUpdatesWhileOffline.current) {
        setSyncStateBoth("conflict");
        hadLocalUpdatesWhileOffline.current = false;
      } else {
        console.log("[StudioContext] syncState -> synced");
        setSyncStateBoth("synced");
      }
    };

    // Doc update -- distinguish local vs remote (provider == remote origin)
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      if (origin === provider) return; // remote update -- ignore
      if (syncStateRef.current === "reconnecting") {
        // Accumulate local changes while offline
        hadLocalUpdatesWhileOffline.current = true;
      } else {
        setSyncStateBoth("dirty");
      }
    };

    provider.on("status", onStatus);
    provider.on("connection-close", onConnectionLost);
    provider.on("connection-error", onConnectionLost);
    provider.on("sync", onSync);
    doc.on("update", onUpdate);

    return () => {
      provider.off("status", onStatus);
      provider.off("connection-close", onConnectionLost);
      provider.off("connection-error", onConnectionLost);
      provider.off("sync", onSync);
      doc.off("update", onUpdate);
    };
  }, [provider, techPack, setSyncStateBoth]);

  // ---- Client-side REST load on mount ----
  // E-1 v2: load saved JSON from /api/studio/techpacks/:id IMMEDIATELY on mount
  // (un-gated — this REST/doc_json path is the PRIMARY content source; the WS
  // doc_yjs path is unreliable, see DEBT E-4 frame bug). Two safety properties:
  //   • SEED-ONLY-IF-EMPTY — never overwrite content already present (a peer's
  //     live edits, or WS-synced state). `!map.has(k)` protects identity
  //     (style_number/title); `arr.length === 0` protects rows.
  //   • Sets `hydrated` (gates the form render so self-seeders can't pre-empt the
  //     load) and `loadOk` (false on failure → degraded read-only; autoSave
  //     refuses to persist over unknown DB state).
  const loadAttemptedRef = useRef(false);
  useEffect(() => {
    if (loadAttemptedRef.current) return;
    loadAttemptedRef.current = true;
    const markHydrated = (ok: boolean) => {
      techPack.loadOk = ok;
      techPack.hydrated = true;
      setHydrated(true);
    };
    let cancelled = false;
    // Degraded fallback: if the request hangs, unblock the UI after 8s but keep
    // loadOk=false so we never persist over content we failed to load.
    const timer = setTimeout(() => { if (!cancelled) markHydrated(false); }, 8000);
    // BUGFIX (right-side form empty, 2026-09-21): this used to read the token
    // straight from localStorage.getItem("studio_token") instead of going
    // through getFreshToken(). auth.ts's getFreshToken has an explicit "Bug F"
    // note for exactly this class of mistake: a session token sitting in
    // localStorage can be expired/within-60s-of-expiry, and the WS connect
    // path (App.tsx) already uses getFreshToken() for that reason. This load
    // effect was the one path that still bypassed it. A stale token here made
    // the GET 401, which this code silently treated as "load failed" ->
    // markHydrated(false) -> hydrated still flips true (loadOk=false) ->
    // TechPackForm's `if (!hydrated)` gate unblocks anyway -> the form renders
    // its empty self-seeded placeholders with no real content, indistinguishable
    // at a glance from a genuine load failure. Independent of the E-4 WS frame
    // bug (server.ts): even with that fixed, an expired token here still skips
    // real content, because this is a separate REST call with its own auth.
    void (async () => {
      const token = await getFreshToken();
      if (cancelled) return;
      if (!token) { markHydrated(false); return; } // no live session: render, but never persist
      const fetchUrl = `/api/studio/techpacks/${encodeURIComponent(roomId)}`;
      fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(r => r.ok
        ? r.json() as Promise<{
            metadata?: Record<string, unknown>;
            bom?: unknown[];
            measurements?: Record<string, unknown>;
            construction?: Record<string, unknown>;
            images?: Record<string, string>;
            activities?: unknown[];
            chat_messages?: unknown[];
          }>
        : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (cancelled) return;
        techPack.ydoc.transact(() => {
          // G1: hydrate EVERY canonical TP collection from the same registry the
          // save paths serialize, closing the reopen-loss of style_id (Style
          // Type/Silhouette), pattern_pieces, construction_instructions, interlining
          // and trim. Maps seed-if-empty + fill-empty-from-server (the size_range /
          // style_number stale-empty reconcile, now generalized to all fields);
          // arrays push only when the local table is still empty.
          hydrateTechPackCollections(techPack, data as Record<string, unknown>);
          if (Array.isArray(data.activities) && data.activities.length > 0) {
            const actArr = techPack.ydoc.getArray("__studio_activity__");
            if (actArr.length === 0) actArr.push(data.activities as BomRow[]);
          }
          if (Array.isArray(data.chat_messages) && data.chat_messages.length > 0) {
            const chatArr = techPack.ydoc.getArray("__studio_chat__");
            if (chatArr.length === 0) chatArr.push(data.chat_messages as BomRow[]);
          }
        });
        clearTimeout(timer);
        markHydrated(true);
        console.log(`[load] Hydrated techpack ${roomId} from REST (seed-if-empty)`);
      })
      .catch(err => {
        console.warn("[load] REST load failed:", err);
        clearTimeout(timer);
        if (!cancelled) markHydrated(false);
        // UX-COPY-1: a 404/403 here means the resource is gone or access was
        // revoked — don't leave a silent empty editor; explain in plain words.
        // Distinguish the two (founder's trigger-logic): 404 = the pack itself
        // was removed/deleted by the owner; 403 = the folder is no longer shared
        // with this member. Instant on-access feedback (B's direct-action path).
        const msg = String((err as Error)?.message ?? "");
        if (/HTTP 404/.test(msg)) notifyDenied("denial_pack_removed");
        else if (/HTTP 403/.test(msg)) notifyDenied("denial_pack_no_access");
      });
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [roomId, techPack]);

  // Load persisted collab messages from DB (B3.1).
  // Change 3: only clear+reseed Yjs chat when the request SUCCEEDS.
  // On network error or non-200: leave existing Yjs data untouched.
  // Empty-but-authoritative (200 + messages:[]) also triggers reseed
  // (the DB IS the truth once B3.1 is deployed).
  useEffect(() => {
    if (!roomId) return;
    interface SafeMsgLoad {
      messageId: string; senderDisplayName: string; senderAvatarColor: string;
      displayText: string; timestampUtc: number; isAi: boolean; layer: string;
    }
    // Same stale-localStorage-token fix as the REST load effect above: use
    // getFreshToken() instead of reading studio_token directly.
    void (async () => {
      const token = await getFreshToken();
      if (!token) return;
      fetch(`/api/studio/techpacks/${encodeURIComponent(roomId)}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() as Promise<{ messages: SafeMsgLoad[] }> : null)
        .then(data => {
          if (data === null) return; // non-200 — leave Yjs untouched (Change 3)
          const chatArr = techPack.ydoc.getArray("__studio_chat__");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const msgs = data.messages.map((m: SafeMsgLoad) => ({
            id:         m.messageId,
            // isAi (machine predicate) drives the AI bubble — never a name match.
            authorId:   m.isAi ? "ai-assistant" : m.senderDisplayName,
            authorName: m.senderDisplayName,  // IP boundary: display name, no UUID
            authorColor: m.senderAvatarColor, // preserves color from DB on reload
            text:       m.displayText,
            ts:         m.timestampUtc,       // Change 2: direct numeric timestamp
            msgType:    "text" as const,
            layer:      m.layer,
          }));
          if (chatArr.length > 0) return; // E-1 v2: already hydrated via load/sync — never clobber
          if (msgs.length === 0) return;
          techPack.ydoc.transact(() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            chatArr.push(msgs as any[]);
          });
          console.log(`[load] Seeded ${msgs.length} collab messages from DB`);
        })
        .catch(() => { /* network error — leave Yjs untouched */ });
    })();
  }, [roomId, techPack]);

  // ---- Language setter ----

  const setLanguage = useCallback(
    (code: string) => {
      setLanguageCodeState(code);
      localStorage.setItem("studio_language", code);
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { lang: code } }));
      // Update our awareness broadcast
      const current = provider.awareness.getLocalState() as RawAwarenessState | null;
      provider.awareness.setLocalState({ ...(current ?? {}), languageCode: code });
    },
    [provider],
  );

  // ---- Active field setter ----

  const setActiveField = useCallback(
    (field: string | null) => {
      const current = provider.awareness.getLocalState() as RawAwarenessState | null;
      provider.awareness.setLocalState({ ...(current ?? {}), activeField: field });
    },
    [provider],
  );

  const value: StudioContextValue = {
    techPack,
    provider,
    userId,
    userName,
    userColor,
    userTimezone,
    userRole,
    userOrigin,
    roomId,
    languageCode,
    setLanguage,
    setActiveField,
    awarenessUsers,
    connectionStatus,
    reconnectCount,
    syncState,
    lastSaved,
    hydrated,
  };

  return <StudioCtx.Provider value={value}>{children}</StudioCtx.Provider>;
}
